<script setup>
import SyntaxHighlightedInput from '@/components/Query/SyntaxHighlightedInput.vue'

defineProps({
  modelValue: {
    type: String,
    default: ''
  },
  placeholder: {
    type: String,
    default: ''
  },
  // Completion field set, passed straight through. Null (the default) means the
  // input falls back to the dataSources store — which is what Event Search
  // wants; Selector Search passes its active tab's columns instead.
  fields: {
    type: Array,
    default: null
  },
  disabled: {
    type: Boolean,
    default: false
  },
  // Swaps the leading icon for a spinner.
  loading: {
    type: Boolean,
    default: false
  },
  clearable: {
    type: Boolean,
    default: false
  },
})

defineEmits(['update:modelValue', 'submit', 'clear'])
</script>

<template>
  <div class="toolbar-query-input">
    <font-awesome-icon
      class="search-icon"
      :icon="['fas', loading ? 'spinner' : 'magnifying-glass']"
      :spin="loading"
    />

    <SyntaxHighlightedInput
      class="query-field"
      :modelValue="modelValue"
      @update:modelValue="$emit('update:modelValue', $event)"
      :placeholder="placeholder"
      :fields="fields"
      :disabled="disabled"
      :multiline="false"
      size="compact"
      @submit="$emit('submit')"
    />

    <button
      v-if="clearable && modelValue"
      type="button"
      class="search-clear"
      title="Clear"
      @click="$emit('clear')"
    >
      <font-awesome-icon :icon="['fas', 'xmark']" />
    </button>
  </div>
</template>

<style scoped>
/*
 * Card-look query chip: a syntax-highlighted, autocompleting field sized to sit
 * on a toolbar line. Distinct from PageToolbar's .toolbar-search-box atom, which
 * is the plain-<input> filter chip the admin pages use.
 *
 * Self-contained by design — it declares its own layout rather than inheriting
 * half of it from a toolbar, so it doesn't depend on where it's rendered.
 */
.toolbar-query-input {
  display: flex;
  align-items: center;
  align-self: center;
  gap: 0.375rem;
  flex: 1;
  min-width: 0;
  box-sizing: border-box;
  /* Inherited from .page-header; the fallback keeps the chip standing on its
     own outside a toolbar. */
  height: var(--toolbar-control-h, 2.5rem);
  padding: 0 0.75rem;
  background: var(--surface-card);
  border: 1px solid var(--border);
  border-radius: var(--radius-lg);
  transition: var(--transition);
}

.toolbar-query-input:focus-within {
  border-color: var(--accent);
}

/* Lands on the child's root via class fallthrough — it carries our scope
   attribute too, so no :deep() needed. */
.query-field {
  flex: 1;
  min-width: 0;
}

.search-icon {
  color: var(--text-disabled);
  font-size: 0.75rem;
  flex-shrink: 0;
}

.search-clear {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 1rem;
  height: 1rem;
  background: transparent;
  border: none;
  border-radius: var(--radius-xs);
  color: var(--text-faint);
  cursor: pointer;
  font-size: 0.6875rem;
  flex-shrink: 0;
  transition: var(--transition-fast);
}

.search-clear:hover {
  color: var(--text-primary);
  background: var(--surface-overlay-3);
}
</style>
